import { updTitle } from "@/lib/receiving";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Plus } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { createDelivery, listOrderDeliveries } from "@/api/receiving";
import { DateInput } from "@/components/DateInput";
import { Button } from "@/components/ui/button";
import { localISODate } from "@/lib/date-range";
import { fmtDate } from "@/lib/format";
import { apiErrorText } from "@/lib/scan-ui/screen";
import { OkkBadge } from "@/pages/receiving/OkkBadge";

/**
 * Приходы (УПД) по заказу поставщику.
 *
 * Единица приёмки — ПОСТАВКА, а не заказ: один заказ приезжает несколькими
 * машинами, и раньше вторая просто не заводилась (unique по заказу). Открытые
 * приходы стоят сверху — вернуться нужно именно в незакрытый.
 */
export function DeliveriesBlock({ orderRef }: { orderRef: string }): React.ReactElement {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [adding, setAdding] = React.useState(false);
  const [number, setNumber] = React.useState("");
  // `toISOString()` — UTC: после 21:00 МСК он отдаёт ВЧЕРАШНИЙ день, и дата
  // УПД у вечерней машины уезжала бы на сутки назад.
  const [date, setDate] = React.useState(() => localISODate());
  const [dateErr, setDateErr] = React.useState("");
  // Водитель приехал без уведомления: документов с ним нет, а товар нужен
  // сразу. Номер тогда подставляет портал, иначе приход не завести вовсе —
  // ключ прихода это (заказ, номер).
  const [noUpd, setNoUpd] = React.useState(false);

  const list = useQuery({
    queryKey: ["receiving-deliveries", orderRef],
    queryFn: () => listOrderDeliveries(orderRef),
    enabled: Boolean(orderRef),
  });

  const create = useMutation({
    mutationFn: () =>
      createDelivery({
        order_ref: orderRef,
        incoming_number: noUpd ? "" : number.trim(),
        incoming_date: date,
        upd_missing: noUpd,
      }),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ["receiving-deliveries", orderRef] });
      navigate(`/receiving/deliveries/${d.id}`);
    },
    onError: (e) => toast.error(apiErrorText(e)),
  });

  return (
    <div className="rounded-lg border bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[13px] font-semibold">Приходы по накладным</span>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="mr-1 size-4" />
            Новый приход
          </Button>
        )}
      </div>

      {adding && (
        <div className="mb-2 space-y-2 rounded-md bg-slate-50 p-2">
          <label className="block text-[11px] text-slate-500">
            Номер УПД поставщика
            <input
              autoFocus
              disabled={noUpd}
              className="mt-1 w-full rounded border p-2 text-sm disabled:bg-slate-100 disabled:text-slate-400"
              value={noUpd ? "" : number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder={noUpd ? "подставит портал" : "напр. 4417"}
            />
          </label>
          <label className="flex items-center gap-2 text-[12px] text-slate-700">
            <input
              type="checkbox"
              className="size-4"
              checked={noUpd}
              onChange={(e) => setNoUpd(e.target.checked)}
            />
            Приехали без документов (нет УПД)
          </label>
          <p className="text-[11px] text-slate-500">
            {noUpd
              ? "Портал подставит «б/н» с датой, чтобы приходы различались. Когда документ доедет — кнопка «Указать УПД» на приходе впишет номер везде сразу."
              : "По номеру накладной приходы и различаются — без него приход не завести."}
          </p>
          <label className="block text-[11px] text-slate-500">
            Дата УПД
            <DateInput
              className="mt-1"
              ariaLabel="Дата УПД"
              value={date}
              onChange={(iso, err) => {
                setDate(iso);
                setDateErr(err);
              }}
            />
          </label>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1"
              disabled={create.isPending || (!noUpd && !number.trim()) || Boolean(dateErr)}
              onClick={() => create.mutate()}
            >
              Открыть приход
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
              Отмена
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-1">
        {list.data?.map((d) => (
          <div key={d.id} className="rounded-md border">
          <button
            className="flex w-full items-center gap-2 p-2 text-left"
            onClick={() => navigate(`/receiving/deliveries/${d.id}`)}
          >
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium">
                {updTitle(d)}
                {d.incoming_date ? ` от ${fmtDate(d.incoming_date)}` : ""}
              </div>
              <div className="text-[11px] text-slate-500">
                позиций: {d.batches.length}
                {d.receipt_number ? ` · накладная ${d.receipt_number}` : ""}
                {d.receipt_posted ? " (проведена)" : ""}
              </div>
            </div>
            <span
              className={
                "rounded px-1.5 py-0.5 text-[10px] " +
                (d.status === "open"
                  ? "bg-amber-100 text-amber-900"
                  : "bg-emerald-100 text-emerald-800")
              }
            >
              {d.status === "open" ? "идёт приёмка" : "завершён"}
            </span>
            <ChevronRight className="size-4 text-slate-400" />
          </button>
          {/* Ссылка на проверку — ОТДЕЛЬНО от кнопки прихода: ссылка внутри
              кнопки — невалидная вложенность, клик ушёл бы не туда. */}
          {d.okk && (
            <div className="px-2 pb-2">
              <OkkBadge okk={d.okk} />
            </div>
          )}
          </div>
        ))}
        {list.data?.length === 0 && !adding && (
          <p className="text-[12px] text-slate-500">
            Приходов пока нет. Машина приехала — откройте приход по её накладной.
          </p>
        )}
      </div>
    </div>
  );
}
